import React, { useMemo } from 'react';
import styled from 'types/theme.types';
import { FormattedMessage } from 'react-intl';
import { CREDENTIALS_TYPE_MAPPING } from 'types/api.types';
import { ProjectSettingsCredentialsSelector } from 'components/projects/settings/credentials/project-settings-credentials-selector';
import Card, { CardDescription } from 'components/common/card';
import ErrorContainer from 'components/common/error-container';
import { Container } from 'components/common/form-components';
import ControlledSaveDiscardButtons from 'components/common/form-controlled/controlled-save-discard-buttons';
import useProjectSettingsCredentialsForm from 'components/projects/settings/credentials/project-settings-credentials-form.hook';
import type { CredentialType, CredentialUsageType } from 'types/api.types';
import uniq from 'lodash/uniq';
import { getCredentialsTypeByUsageType } from 'utils/credential.utils';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';

type CredentialIdByType = Partial<Record<CredentialType, string>>;

type ProjectSettingsCredentialsCardProps = {
  usageType: CredentialUsageType;
  existingProjectCredentials: CredentialIdByType;
  onSave: (formCredentialValues: string[]) => Promise<void>;
  docsHref: string;
  loadingCredentials: boolean;
  credentialsError: any;
  cardHeader?: EmotionJSX.Element;
  disabled?: boolean;
};

const ProjectSettingsCredentialsCard: React.FunctionComponent<ProjectSettingsCredentialsCardProps> = ({
  usageType,
  onSave,
  docsHref,
  existingProjectCredentials,
  loadingCredentials,
  credentialsError,
  cardHeader,
  disabled
}) => {
  const form = useProjectSettingsCredentialsForm({
    usageType,
    projectCredentialsByType: existingProjectCredentials,
    onSave
  });
  const credentialsProviders = useMemo(() => {
    return uniq(getCredentialsTypeByUsageType(usageType).map(cred => CREDENTIALS_TYPE_MAPPING[cred]));
  }, [usageType]);

  if (credentialsError) {
    return <ErrorContainer errorToReport={credentialsError} />;
  }
  const intlPrefix = `projects.settings.menu.` + usageType.toLowerCase();
  const docsLink = (
    <a href={docsHref} target="_blank" rel="noopener noreferrer">
      <FormattedMessage id="link.explanation.more" />
    </a>
  );

  const CardHeader = () => {
    return (
      <>
        <FormattedMessage data-e2e="project-credentials-title" id={intlPrefix + '.title'} />
        <CardDescription>
          <FormattedMessage
            data-e2e="project-credentials-description"
            id={intlPrefix + '.description'}
            values={{ docsLink }}
          />
        </CardDescription>
      </>
    );
  };
  const Content = (
    <>
      <Container>
        {credentialsProviders.map(provider => (
          <ProjectSettingsCredentialsSelector
            disabled={disabled}
            key={provider}
            form={form}
            usageType={usageType}
            credentialProvider={provider}
          />
        ))}
      </Container>

      <FooterContainer>
        <ControlledSaveDiscardButtons
          form={form}
          cancelMessageId={'discard.changes'}
          inContainer={false}
          saveButtonDataE2E={`save-project-${usageType.toLowerCase()}-credentials-btn`}
        />
      </FooterContainer>
    </>
  );

  return (
    <Card title={cardHeader || <CardHeader />} loading={loadingCredentials}>
      {Content}
    </Card>
  );
};

const FooterContainer = styled.div`
  text-align: right;
  margin-top: 40px;

  button,
  a {
    width: 210px;
  }

  button:first-of-type {
    margin-right: 8px;
    margin-top: 16px;
  }
`;

export default ProjectSettingsCredentialsCard;
